// This file configures the initialization of Sentry and PostHog on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/order/:id/confirmed\",\"regex\":\"^/order/([^/]+)/confirmed$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/:slug\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/account/@dashboard/orders/details/:id\",\"regex\":\"^/account/@dashboard/orders/details/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/categories/:category*\",\"regex\":\"^/categories/(.+)$\",\"paramNames\":[\"category\"],\"hasOptionalPrefix\":false},{\"path\":\"/collections/:handle\",\"regex\":\"^/collections/([^/]+)$\",\"paramNames\":[\"handle\"],\"hasOptionalPrefix\":false},{\"path\":\"/consultation/:slug\",\"regex\":\"^/consultation/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/products/:handle\",\"regex\":\"^/products/([^/]+)$\",\"paramNames\":[\"handle\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/basket\"},{\"path\":\"/checkout\"},{\"path\":\"/\"},{\"path\":\"/account/@dashboard\"},{\"path\":\"/account/@dashboard/addresses\"},{\"path\":\"/account/@dashboard/orders\"},{\"path\":\"/account/@dashboard/payment-methods\"},{\"path\":\"/account/@dashboard/profile\"},{\"path\":\"/account/@login\"},{\"path\":\"/complaints\"},{\"path\":\"/consultation/start\"},{\"path\":\"/contact\"},{\"path\":\"/cookie-policy\"},{\"path\":\"/cookie-settings\"},{\"path\":\"/faqs\"},{\"path\":\"/how-it-works\"},{\"path\":\"/privacy\"},{\"path\":\"/search\"},{\"path\":\"/shipping\"},{\"path\":\"/terms\"},{\"path\":\"/terms-of-sale\"},{\"path\":\"/auth/verify-email\"}],\"isrRoutes\":[\"/:slug\",\"/categories/:category*\",\"/collections/:handle\",\"/products/:handle\"]}";globalThis["_sentryNextJsVersion"] = "16.2.11";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import {
  CONSENT_COOKIE_NAME,
  CONSENT_STORAGE_KEY,
  CONSENT_UPDATED_EVENT,
} from '@lib/analytics/constants';
import { flushPendingPosthogEvents } from '@lib/analytics/posthog';
import {
  BROWSER_INJECTED_PREFIXES,
  makeClientBeforeSend,
} from '@lib/analytics/sentry-client-before-send';
import {
  scrubPathnameIfHealthSensitive,
  scrubUrlIfHealthSensitive,
} from '@lib/analytics/url-scrubbing';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';

// ---------------------------------------------------------------------------
// Sentry
// ---------------------------------------------------------------------------

const REPLAY_SESSION_SAMPLE_RATE = 0.1;
const REPLAY_ON_ERROR_SAMPLE_RATE = 1.0;

Sentry.init({
  dsn: 'https://2a2fe40b259c8e8d30531860b06bcc11@o4508495368683520.ingest.de.sentry.io/4511032113102928',

  // Disable in local development.
  enabled: process.env.NODE_ENV !== 'development',

  // Use the deploy-specific environment (development / staging / production)
  // rather than NODE_ENV, which is always "production" in Docker containers.
  environment:
    process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV,

  // Tag every error/transaction with the Git SHA so Sentry can correlate
  // issues to specific deploys and track regressions across releases.
  release: process.env.NEXT_PUBLIC_BUILD_SHA,

  // Replay integration is added dynamically once analytics consent is
  // granted (see updateSentryReplay below). No recording before consent.

  // Sample 10% of traces in production (was 1.0 — every page load).
  // 10% still gives good coverage while cutting ~615 ms of scripting cost on mobile.
  tracesSampleRate: 0.1,

  // Enable logs to be sent to Sentry
  enableLogs: true,

  // Drop events whose stack frames originate from browser-injected scripts
  // (extensions, Reader Mode, etc.) before they reach beforeSend.
  denyUrls: BROWSER_INJECTED_PREFIXES.map((p) => new RegExp(`^${p}`)),

  // Browser autofill widgets (triggered on Clearpay referral UTM traffic) set up
  // a MutationObserver that fires during Next.js Turbopack hydration before the
  // widget's internal form manager is initialised — this is third-party noise,
  // not application code.
  //
  // Playwright serialises page objects as <Page url='...' name='...'> in error
  // messages during E2E runs — the closing quote anchors this to that specific
  // format and avoids suppressing unrelated production errors.
  ignoreErrors: [/startGetFields/, /<Page url=(['"])[^'"]*\1/],

  // Replay sample rates — applied when the replayIntegration is active.
  replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
  replaysOnErrorSampleRate: REPLAY_ON_ERROR_SAMPLE_RATE,

  // Enable PII collection (IP, user-agent, cookies) so Sentry can attach it
  // when consent is granted. The beforeSend callback below strips PII from
  // events when the user has not consented to analytics cookies.
  sendDefaultPii: true,

  // Drop E2E events, filter browser-injected frames, and strip PII without consent.
  beforeSend: makeClientBeforeSend(
    () => document.cookie,
    hasAnalyticsConsentLocal,
  ),
});

// ---------------------------------------------------------------------------
// Consent helpers for Sentry
// ---------------------------------------------------------------------------

/**
 * Read current analytics (Statistics) consent from localStorage / cookie.
 *
 * Used by Sentry `beforeSend` (strip PII without consent) AND PostHog
 * `sanitize_properties` (scrub health-revealing URLs without consent). The
 * whole site is healthcare, so Statistics consent is consent to product /
 * condition URLs reaching analytics; without it we scrub.
 */
function hasAnalyticsConsentLocal(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);

    if (raw) {
      const prefs = JSON.parse(raw);

      return prefs?.analytics === true;
    }
  } catch {
    // fall through
  }
  try {
    return document.cookie.includes(`${CONSENT_COOKIE_NAME}=all`);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Sentry Replay — gated on analytics consent
// ---------------------------------------------------------------------------

type SentryReplayIntegration = {
  start: () => void;
  startBuffering: () => void;
  stop: () => void;
};

// Replicate addIntegration sampling logic: session-sampled users get full
// recording, the rest get buffering so errors still trigger a replay.
function startOrBufferReplay(replay: SentryReplayIntegration): void {
  if (Math.random() < REPLAY_SESSION_SAMPLE_RATE) {
    replay.start();
  } else if (REPLAY_ON_ERROR_SAMPLE_RATE > 0) {
    replay.startBuffering();
  }
}

// A single in-flight lazy-load, shared across rapid consent events so quick
// re-grants never fetch the Replay bundle from the CDN twice or add the
// integration twice.
let replayLoad: Promise<void> | undefined;

// Load Replay lazily from the Sentry CDN (browser.sentry-cdn.com — allowed by
// our CSP) instead of statically referencing `Sentry.replayIntegration()`,
// which bundles Replay's (large) code into the main client chunk that runs
// during hydration. Replay is only ever needed once a consented user is
// recording, so keeping it off the critical path cuts mobile INP/TBT.
async function loadAndAddReplay(): Promise<void> {
  const replayIntegration =
    await Sentry.lazyLoadIntegration('replayIntegration');

  // Re-check consent AFTER the async CDN fetch: the user may have withdrawn it
  // while the bundle was loading. `addIntegration` auto-starts recording, so a
  // grant that has since been revoked must never be allowed to record.
  if (!hasAnalyticsConsentLocal()) {
    return;
  }

  // A concurrent consent event may have added it while we awaited — never add
  // a second Replay integration.
  if (Sentry.getClient()?.getIntegrationByName('Replay')) {
    return;
  }

  Sentry.addIntegration(replayIntegration());
}

function startSentryReplay(replay: SentryReplayIntegration | undefined): void {
  if (replay) {
    startOrBufferReplay(replay);
    return;
  }

  // Not loaded yet. Coalesce concurrent consent grants onto one CDN fetch;
  // clear the guard once it settles so a later grant (after a revoke) retries.
  if (!replayLoad) {
    replayLoad = loadAndAddReplay()
      .catch(() => undefined)
      .finally(() => {
        replayLoad = undefined;
      });
  }
}

function stopSentryReplay(replay: SentryReplayIntegration | undefined): void {
  replay?.stop();
}

/**
 * Enable or disable Sentry session replay based on analytics consent.
 *
 * On first grant: adds the replayIntegration (never added without consent).
 * On re-grant after withdrawal: restarts the existing integration.
 * On withdrawal: stops the integration and clears the replay buffer.
 */
function updateSentryReplay(hasConsent: boolean): void {
  const client = Sentry.getClient();
  if (!client) {
    return;
  }

  const replay = client.getIntegrationByName('Replay') as
    | SentryReplayIntegration
    | undefined;

  if (hasConsent) {
    startSentryReplay(replay);
  } else {
    stopSentryReplay(replay);
  }
}

updateSentryReplay(hasAnalyticsConsentLocal());

window.addEventListener(CONSENT_UPDATED_EVENT, () => {
  updateSentryReplay(hasAnalyticsConsentLocal());
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;

// ---------------------------------------------------------------------------
// PostHog helpers
// ---------------------------------------------------------------------------

// Requests matching any of these patterns contain credentials, medical data,
// or third-party analytics payloads that must never appear in session replays.
const DROP_REQUEST_PATTERNS = [
  'stripe.com',
  '/auth/',
  '/consultation',
  '/api/order-messaging',
] as const;

// Braze SDK clusters: EU (fra), US (iad/dwh/nrt) — match by subdomain suffix.
const BRAZE_SDK_PATTERN = /sdk\.(fra|iad|dwh|nrt)-[0-9]+\.braze\.(eu|com)/;

function shouldDropNetworkRequest(name: string | undefined): boolean {
  if (!name) {
    return false;
  }
  return (
    DROP_REQUEST_PATTERNS.some((p) => name.includes(p)) ||
    BRAZE_SDK_PATTERN.test(name)
  );
}

function scrubStringProp(
  props: Record<string, unknown>,
  key: string,
  scrub: (v: string) => string,
): void {
  if (typeof props[key] === 'string') {
    props[key] = scrub(props[key] as string);
  }
}

// ---------------------------------------------------------------------------
// PostHog
// ---------------------------------------------------------------------------

const posthogToken = process.env.NEXT_PUBLIC_POSTHOG_TOKEN;
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;

if (posthogToken && posthogHost) {
  const buildSha = process.env.NEXT_PUBLIC_BUILD_SHA;

  // Deferred via requestIdleCallback so PostHog init does not run on the
  // synchronous bootstrap path. NB: posthog-js does NOT queue capture()
  // calls made before init() — it drops them with a console warning. The
  // `posthogCapture` wrapper buffers pre-init events instead, and the
  // `loaded` callback below replays them, so events fired during the idle
  // window (e.g. `purchase` on a fresh confirmation-page load) are not lost.
  //
  // Consent is read inside the callback so that users who accept the cookie
  // banner before the idle window fires are initialised with capturing
  // already enabled — the posthog.__loaded guard in consent.ts skips the
  // opt_in_capturing() call when PostHog is not yet initialised, but since
  // we re-read localStorage here the init config reflects the current state.
  //
  // timeout: 3000 ensures init runs no later than 3 s even on a busy mobile
  // main thread (rIC may starve on low-end devices without a timeout).
  function scheduleSessionRecording() {
    let started = false;
    const events = ['click', 'keydown', 'scroll', 'touchstart'] as const;

    const start = () => {
      if (started) return;
      started = true;
      clearTimeout(fallback);
      for (const e of events) {
        window.removeEventListener(e, start);
      }
      // Re-check consent: the user may have withdrawn it between
      // scheduleSessionRecording() and this callback firing (e.g. the
      // same click that triggered withdrawal also triggers this listener,
      // but React's handler runs first).
      if (!hasAnalyticsConsentLocal()) return;
      posthog.startSessionRecording();
    };

    const fallback = setTimeout(start, 3_000);

    for (const e of events) {
      window.addEventListener(e, start, { passive: true });
    }
  }

  const initPostHog = () => {
    // Read existing consent to determine whether PostHog should start
    // capturing immediately or wait for consent. Check localStorage first
    // (detailed prefs), fall back to the `_consent` cookie.
    const hasAnalyticsConsent = hasAnalyticsConsentLocal();

    posthog.init(posthogToken, {
      // Opt in to PostHog's dated config defaults rather than legacy behaviour.
      // '2025-05-24' flips the `capture_pageview` default to 'history_change'
      // (see below). Pinned to an explicit date so future SDK default changes are
      // adopted deliberately, not silently on upgrade.
      defaults: '2025-05-24',

      // Route all capture/decide/flags requests through the Next.js rewrite
      // proxy at /ingest → PostHog host. This avoids ad-blocker interference.
      // The rewrite rules are defined in next.config.js.
      api_host: '/ingest',

      // Respect cookie consent: start opted out unless the user has already
      // granted analytics consent. The cookie consent banner will call
      // posthog.opt_in_capturing() / posthog.opt_out_capturing() on interaction.
      opt_out_capturing_by_default: !hasAnalyticsConsent,

      // ui_host tells PostHog where to load toolbar assets from (not ingestion).
      // This must point to the real PostHog host, not the proxy.
      ui_host: posthogHost,

      // Capture clicks automatically. Elements with the `ph-no-capture` CSS
      // class are excluded from both autocapture AND session replay.
      // dom_event_allowlist restricts autocapture to click events only —
      // change/input/submit events are handled by manual trackX() calls in
      // events.ts, so intercepting them here adds listener overhead with no
      // analytics benefit.
      autocapture: {
        element_attribute_ignorelist: ['aria-label', 'title', 'placeholder'],
        dom_event_allowlist: ['click'],
      },

      // Always disable session recording at init — the recorder module
      // (posthog-recorder.js, ~57 KiB) is deferred until the first user
      // interaction or 3 s, whichever comes first. scheduleSessionRecording()
      // below handles the lazy start for consented users.
      disable_session_recording: true,

      // -----------------------------------------------------------------------
      // Session replay privacy — defence in depth
      //
      // Layer 1: All input values masked globally (PostHog default, explicit).
      // Layer 2: Elements with `ph-no-capture` are blocked entirely (replaced
      //          with a same-size placeholder in recordings). Applied to:
      //          - Consultation forms (medical/NHS data)
      //          - Checkout address + payment forms
      //          - Account forms (login, register, profile, addresses)
      //          - Order confirmation PII (addresses, card last 4)
      // Layer 3: Network request/response bodies scrubbed of PII patterns.
      // Layer 4: Stripe card inputs are cross-origin iframes — PostHog cannot
      //          access them architecturally, but we block the wrapper anyway.
      // -----------------------------------------------------------------------
      session_recording: {
        // Mask all <input>, <textarea>, <select> values in recordings.
        // This is PostHog's default, but we set it explicitly so it can't be
        // accidentally disabled by a future config change.
        maskAllInputs: true,

        // Network capture: scrub request/response bodies that may contain PII,
        // payment data, or medical information. PostHog auto-scrubs some
        // patterns (credit cards, SSN, passwords) but we add explicit handling
        // for our Medusa API routes.
        maskCapturedNetworkRequestFn: (request) => {
          if (shouldDropNetworkRequest(request.name)) {
            return null;
          }

          // Scrub request/response bodies on customer and cart routes
          // (contain addresses, email, phone, payment info).
          if (
            request.name?.includes('/store/customers') ||
            request.name?.includes('/store/carts')
          ) {
            request.requestBody = undefined;
            request.responseBody = undefined;
          }

          return request;
        },
      },

      // Capture performance data (web vitals, network requests).
      // Note: LCP entries may not be captured when init is deferred past the
      // LCP event — this affects PostHog's own web vitals dashboard but has
      // no impact on CrUX / Lighthouse scores, which measure independently.
      capture_performance: true,

      // Capture pageviews on SPA route changes, not just full page loads. This is
      // the recommended mode for a Next.js App Router app (and the '2025-05-24'
      // default): posthog-js listens to the History API so client-side navigations
      // are counted. There is no manual `$pageview` capture elsewhere, so this is
      // the single source of pageviews — no double-counting. NB: this changes
      // pageview counts vs the previous `true` (full-load-only) behaviour.
      capture_pageview: 'history_change',
      capture_pageleave: true,

      // Scrub product/category URLs from PostHog events when analytics
      // (Statistics) consent is not granted. Product handles (e.g.
      // /sildenafil-50mg) and category handles (e.g. /erectile-dysfunction)
      // reveal health conditions.
      sanitize_properties: (properties, _eventName) => {
        if (hasAnalyticsConsentLocal()) {
          return properties;
        }

        // Scrub URL-bearing and pathname properties on all events so that
        // product/condition handles (e.g. /sildenafil-50mg) do not reach
        // PostHog without Statistics consent.
        scrubStringProp(properties, '$current_url', scrubUrlIfHealthSensitive);
        scrubStringProp(properties, '$referrer', scrubUrlIfHealthSensitive);
        scrubStringProp(
          properties,
          '$initial_current_url',
          scrubUrlIfHealthSensitive,
        );
        scrubStringProp(
          properties,
          '$pathname',
          scrubPathnameIfHealthSensitive,
        );
        scrubStringProp(
          properties,
          '$initial_pathname',
          scrubPathnameIfHealthSensitive,
        );

        return properties;
      },

      // Capture unhandled exceptions and promise rejections into PostHog error
      // tracking. Until this was enabled the project ingested zero `$exception`
      // events, so client-side failures (e.g. a hung consultation boot) were
      // invisible. Events flow through the same opt-out + `sanitize_properties`
      // pipeline as everything else, so health-revealing URLs are scrubbed
      // without Statistics consent. Caught errors (React error boundaries, the
      // boot-timeout fallback) are reported explicitly via
      // `posthogCaptureException` since they never surface as unhandled.
      capture_exceptions: true,

      // Persist identity across sessions.
      persistence: 'localStorage+cookie',

      // Surveys disabled — not in use right now.
      disable_surveys: true,

      // Don't fetch feature flags on the initial page load (MWP-799). Flag-driven
      // behaviour in the storefront is evaluated server-side; the only client-side
      // flags are non-critical (`analytics_debug`, a debug toggle, and
      // `enable_back_in_stock`, which is kept OFF until implemented). Deferring the
      // first-load fetch removes a billable flag request from every page load —
      // most impactful for single-page anonymous visits. posthog-js still loads
      // flags on later navigations and whenever `reloadFeatureFlags()` is called,
      // so the client hooks resolve normally once the user moves through the site.
      advanced_disable_feature_flags_on_first_load: true,

      // Replay events buffered by `posthogCapture` while init was deferred
      // (posthog-js drops pre-init capture() calls). If the user is opted
      // out, posthog-js discards the replayed events itself — consent
      // semantics are unchanged.
      loaded: () => {
        flushPendingPosthogEvents();
      },
    });

    // Attach the build SHA to every PostHog event so analytics and session
    // replays can be correlated to a specific deploy. This is a "super
    // property" — it persists across sessions and is sent with every event.
    if (buildSha) {
      posthog.register({ build_sha: buildSha });
    }

    if (hasAnalyticsConsent) {
      scheduleSessionRecording();
    }
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(initPostHog, { timeout: 3000 });
  } else {
    // Fallback for Safari < 16.4 — defer by one macrotask so init is at
    // least off the synchronous module-evaluation path.
    setTimeout(initPostHog, 0);
  }
}
